/**
 * DojoOS standard roles — aligned with real dojo operations.
 * @typedef {object} DojoOsRole
 * @property {string} id
 * @property {string} labelFr
 * @property {string} labelEn
 * @property {string} icon
 * @property {string} descriptionFr
 * @property {string} descriptionEn
 * @property {string[]} defaultModules
 * @property {'full'|'manager'|'operational'|'limited'|'read'|'federation'} accessLevel
 * @property {'active'|'coming_soon'|'future'} availability
 */

/** @type {DojoOsRole[]} */
export const DOJOOS_ROLES = [
  {
    id: "guest",
    labelFr: "Visiteur",
    labelEn: "Guest",
    icon: "🌐",
    descriptionFr: "Visiteur public — consultation limitée.",
    descriptionEn: "Public visitor — limited access.",
    defaultModules: ["help"],
    accessLevel: "read",
    availability: "active",
  },
  {
    id: "admin",
    labelFr: "Hanshi/Admin",
    labelEn: "Hanshi/Admin",
    icon: "🐉",
    descriptionFr: "Administration plateforme et multi-dojo.",
    descriptionEn: "Platform and multi-dojo administration.",
    defaultModules: [
      "dashboard",
      "organizations",
      "dojos",
      "instructors",
      "assistants",
      "students",
      "parents",
      "classes",
      "events",
      "documents",
      "communications",
      "settings",
      "help",
      "training",
    ],
    accessLevel: "full",
    availability: "active",
  },
  {
    id: "manager",
    labelFr: "Sensei",
    labelEn: "Sensei",
    icon: "🥋",
    descriptionFr: "Direction technique — cours, progression, examens.",
    descriptionEn: "Technical lead — classes, progress, exams.",
    defaultModules: [
      "dashboard",
      "dojos",
      "classes",
      "students",
      "attendance",
      "progress",
      "exams",
      "events",
      "documents",
      "communications",
      "help",
      "training",
    ],
    accessLevel: "manager",
    availability: "active",
  },
  {
    id: "assistant",
    labelFr: "Sempai / assistant",
    labelEn: "Sempai / assistant",
    icon: "🤝",
    descriptionFr: "Aide aux cours et groupes assignés.",
    descriptionEn: "Assists classes and assigned groups.",
    defaultModules: ["classes", "students", "attendance", "documents", "help", "training"],
    accessLevel: "operational",
    availability: "coming_soon",
  },
  {
    id: "player",
    labelFr: "Étudiant",
    labelEn: "Student",
    icon: "🎓",
    descriptionFr: "Élève — cours, progression, documents.",
    descriptionEn: "Student — classes, progress, documents.",
    defaultModules: [
      "dashboard",
      "students",
      "dojos",
      "instructors",
      "classes",
      "events",
      "progress",
      "documents",
      "help",
      "training",
    ],
    accessLevel: "limited",
    availability: "active",
  },
  {
    id: "parent",
    labelFr: "Parent",
    labelEn: "Parent",
    icon: "👨‍👩‍👧",
    descriptionFr: "Suivi enfants, horaires, communications.",
    descriptionEn: "Child tracking, schedules, communications.",
    defaultModules: ["students", "schedule", "progress", "events", "communications", "documents", "help"],
    accessLevel: "read",
    availability: "coming_soon",
  },
  {
    id: "federation",
    labelFr: "Fédération",
    labelEn: "Federation",
    icon: "🏛️",
    descriptionFr: "Certifications, grades, compétitions fédérales.",
    descriptionEn: "Certifications, grades, federation competitions.",
    defaultModules: ["organizations", "dojos", "belts", "competitions", "reports"],
    accessLevel: "federation",
    availability: "future",
  },
];

/**
 * @param {string} roleId
 * @returns {DojoOsRole|undefined}
 */
export function findDojoOsRole(roleId) {
  return DOJOOS_ROLES.find((r) => r.id === roleId);
}

/**
 * @param {string[]} [enabledRoles]
 * @returns {DojoOsRole[]}
 */
export function filterDojoOsRoles(enabledRoles) {
  if (!enabledRoles?.length) return DOJOOS_ROLES.filter((r) => r.id !== "guest");
  const set = new Set(enabledRoles);
  return DOJOOS_ROLES.filter((r) => set.has(r.id));
}
